import { useState } from 'react';
import { useApiErrorHandler } from '@/lib/useApiErrorHandler';
import type { PlayerProfile } from '@swiss-magic/shared-types';

interface UseViewProfileDialogParams<T> {
  fetchProfile: (target: T) => Promise<PlayerProfile>;
}

export function useViewProfileDialog<T>({
  fetchProfile,
}: UseViewProfileDialogParams<T>) {
  const [viewProfileTarget, setViewProfileTarget] = useState<T | null>(null);
  const [viewProfileData, setViewProfileData] = useState<PlayerProfile | null>(
    null,
  );
  const [viewProfileLoading, setViewProfileLoading] = useState(false);

  const handleError = useApiErrorHandler();

  async function openProfileViewer(target: T) {
    setViewProfileTarget(target);
    setViewProfileData(null);
    setViewProfileLoading(true);
    try {
      const profile = await fetchProfile(target);
      setViewProfileData(profile);
    } catch (err) {
      handleError(err, 'Failed to load profile');
      setViewProfileTarget(null);
    } finally {
      setViewProfileLoading(false);
    }
  }

  function closeProfileViewer() {
    setViewProfileTarget(null);
    setViewProfileData(null);
    setViewProfileLoading(false);
  }

  return {
    viewProfileTarget,
    viewProfileData,
    viewProfileLoading,
    openProfileViewer,
    closeProfileViewer,
  };
}
